import * as z from "zod";
import {
  array,
  boolean,
  date,
  discriminatedUnion,
  literal,
  number,
  object,
  string,
  enum as zEnum,
} from "zod";
import {
  ActionType,
  ContentType,
  IntentType,
  TrackOrderUiState,
} from "../../conversation";
import {
  CONCIERGE_CONVERSATION_CONSTANTS as C,
  SenderType,
} from "../concierge-conversation-message-definition";

// Base message without auto-generated fields
const BaseMessageSchema = object({
  [C.CONTENT_TYPE_KEY]: zEnum(ContentType),
  senderType: zEnum(SenderType),
  systemPromptId: string().optional(),
  toolCallId: string().optional(),
});

// Base actionable content message schema
const BaseActionableContentMessageSchema = BaseMessageSchema.extend({
  actionType: zEnum(ActionType).optional(),
  intent: zEnum(IntentType).optional(),
});

// Auto-generated fields schema
const AutoGeneratedFieldsSchema = object({
  _id: string(),
  createdAt: date(),
  updatedAt: date(),
});

// Text message schema
const TextMessageSchema = BaseMessageSchema.extend({
  [C.CONTENT_TYPE_KEY]: literal(ContentType.TEXT),
  content: string(),
}).merge(AutoGeneratedFieldsSchema);

// Product details schema
const ProductVariantSchema = object({
  id: string(),
  gid: string(),
  availableForSale: z.boolean(),
  price: object({ amount: string(), currencyCode: string() }),
  title: string(),
});

const ProductDetailsSchema = object({
  id: string(),
  variantId: string(),
  title: string(),
  description: string(),
  seo: object({
    title: string().optional(),
    description: string().optional(),
  }).optional(),
  variants: object({ nodes: array(ProductVariantSchema) }),
  url: string().optional(),
});

// Product details message schema
const ProductDetailsMessageSchema = BaseActionableContentMessageSchema.extend({
  [C.CONTENT_TYPE_KEY]: literal(ContentType.TOOL_CALL_PRODUCT_INFO),
  content: ProductDetailsSchema,
}).merge(AutoGeneratedFieldsSchema);

// Back in stock notification UI tool message schema
const BackInStockNotificationUiToolMessageSchema =
  BaseActionableContentMessageSchema.extend({
    [C.CONTENT_TYPE_KEY]: literal(
      ContentType.SUBSCRIBE_TO_BACK_IN_STOCK_NOTIFICATION_UI,
    ),
    content: object({
      productId: string(),
      variantIds: array(string()),
      customerEmail: z.union([z.string(), z.null()]),
      subscribed: boolean(),
    }),
  }).merge(AutoGeneratedFieldsSchema);

// Resend return label UI tool message schema
const ResendReturnLabelUiToolMessageSchema = BaseMessageSchema.extend({
  [C.CONTENT_TYPE_KEY]: literal(ContentType.RESEND_RETURN_LABEL_UI),
  content: z.union([
    object({ orderEmail: z.string(), orderId: z.string() }),
    object({ orderEmail: z.union([z.string(), z.null()]), orderId: z.null() }),
  ]),
}).merge(AutoGeneratedFieldsSchema);

// Discount code message schema
const DiscountCodeMessageSchema = BaseMessageSchema.extend({
  [C.CONTENT_TYPE_KEY]: literal(ContentType.DISCOUNT_CODE),
  content: object({ discountCode: string() }),
}).merge(AutoGeneratedFieldsSchema);

// Cart message schema
const CartMessageSchema = BaseActionableContentMessageSchema.extend({
  [C.CONTENT_TYPE_KEY]: literal(ContentType.CART_ITEMS),
  content: object({
    [C.PRODUCT_ID_KEY]: number(),
    [C.VARIANT_ID_KEY]: number(),
    [C.QUANTITY_KEY]: number(),
    [C.PRODUCT_TITLE_KEY]: string().optional(),
  }),
}).merge(AutoGeneratedFieldsSchema);

// Email input message schema
const EmailInputMessageSchema = BaseActionableContentMessageSchema.extend({
  [C.CONTENT_TYPE_KEY]: literal(ContentType.EMAIL_INPUT),
  content: object({ customerEmail: z.union([z.string().email(), z.null()]) }),
}).merge(AutoGeneratedFieldsSchema);

// Product display message schema
const TrackableFulfillmentSchema = array(
  object({
    orderCreatedAt: string().optional(),
    estimatedDeliveryDate: string().optional().nullable(),
    trackingDetails: array(
      object({ status: string(), datetime: string() }),
    ).optional(),
    trackingUrl: string().optional(),
    redoTrackingPageLink: string().optional(),
    carrier: string().optional(),
    trackingNumber: string().optional(),
    lineItems: array(
      object({
        productId: string(),
        productTitle: string(),
        variantId: string().optional(),
        variantTitle: string().optional(),
        url: string().optional(),
        price: object({ amount: number(), currencyCode: string() }),
        priceRange: object({
          minVariantPrice: object({ amount: number(), currencyCode: string() }),
          maxVariantPrice: object({ amount: number(), currencyCode: string() }),
        }).optional(),
        imageUrl: string().optional(),
        handle: z.preprocess(
          (val) => (typeof val === "object" && val !== null ? undefined : val),
          string().optional(),
        ),
      }),
    ),
  }),
);
export type TrackableFulfillmentType = z.infer<
  typeof TrackableFulfillmentSchema
>;

const DisplayOrderTrackingInfoMessageSchema =
  BaseActionableContentMessageSchema.extend({
    [C.CONTENT_TYPE_KEY]: literal(ContentType.DISPLAY_ORDER_TRACKING_INFO),
    content: TrackableFulfillmentSchema,
  }).merge(AutoGeneratedFieldsSchema);
export type RpcDisplayOrderTrackingInfoMessageType = z.infer<
  typeof DisplayOrderTrackingInfoMessageSchema
>;

const TrackOrderUiMessageSchema = BaseMessageSchema.extend({
  [C.CONTENT_TYPE_KEY]: literal(ContentType.TRACK_ORDER_UI),
  content: z.union([
    object({ state: literal(TrackOrderUiState.ORDER_LOOKUP) }),
    object({
      state: literal(TrackOrderUiState.FOUND_ORDER),
      orderId: z.string(),
      email: z.string(),
      trackableFulfillmentsToDisplay: TrackableFulfillmentSchema,
    }),
  ]),
}).merge(AutoGeneratedFieldsSchema);

// Function call schema
const FunctionCallSchema = object({ name: string(), arguments: string() });

// Tool call message schema
const ToolCallMessageSchema = BaseActionableContentMessageSchema.extend({
  [C.CONTENT_TYPE_KEY]: literal(ContentType.TOOL_CALL),
  content: array(
    object({
      id: string(),
      type: literal("function"),
      function: FunctionCallSchema,
    }),
  ),
}).merge(AutoGeneratedFieldsSchema);

// Tool call error message schema
const ToolCallErrorMessageSchema = BaseMessageSchema.extend({
  [C.CONTENT_TYPE_KEY]: literal(ContentType.TOOL_CALL_ERROR),
  content: object({ error: string() }),
}).merge(AutoGeneratedFieldsSchema);

const ContactSupportUiMessageSchema = BaseMessageSchema.extend({
  [C.CONTENT_TYPE_KEY]: literal(ContentType.CONTACT_SUPPORT_UI),
  content: z.object({}),
}).merge(AutoGeneratedFieldsSchema);

// Discriminated union of all message types
export const conciergeConversationMessageSchema = discriminatedUnion(
  C.CONTENT_TYPE_KEY,
  [
    TextMessageSchema,
    ProductDetailsMessageSchema,
    BackInStockNotificationUiToolMessageSchema,
    ResendReturnLabelUiToolMessageSchema,
    CartMessageSchema,
    EmailInputMessageSchema,
    ContactSupportUiMessageSchema,
    DisplayOrderTrackingInfoMessageSchema,
    TrackOrderUiMessageSchema,
    DiscountCodeMessageSchema,
    ToolCallMessageSchema,
    ToolCallErrorMessageSchema,
  ],
);

export type ConciergeConversationMessage = z.infer<
  typeof conciergeConversationMessageSchema
>;

export type ActionableContentMessage = z.infer<
  typeof BaseActionableContentMessageSchema
>;
export type TextMessage = z.infer<typeof TextMessageSchema>;
export type ProductDetailsMessage = z.infer<typeof ProductDetailsMessageSchema>;
export type CartMessage = z.infer<typeof CartMessageSchema>;
export type EmailInputMessage = z.infer<typeof EmailInputMessageSchema>;
export type TrackOrderMessage = z.infer<typeof TrackOrderUiMessageSchema>;
export type BackInStockNotificationUiToolMessage = z.infer<
  typeof BackInStockNotificationUiToolMessageSchema
>;
export type ResendReturnLabelUiToolMessage = z.infer<
  typeof ResendReturnLabelUiToolMessageSchema
>;
export type DiscountCodeMessage = z.infer<typeof DiscountCodeMessageSchema>;
export type ToolCallMessage = z.infer<typeof ToolCallMessageSchema>;

export function isTextMessage(
  message: ConciergeConversationMessage,
): message is TextMessage {
  return message.contentType === ContentType.TEXT;
}

export function isToolResponseMessage(
  message: ConciergeConversationMessage,
): boolean {
  return message.senderType === SenderType.TOOL;
}

export enum ConciergeTrackOrderResult {
  FOUND_TRACKABLE_FULFILLMENTS = "FOUND_TRACKABLE_FULFILLMENTS",
  ERROR = "ERROR",
}
